import React, { useState } from 'react';
import { Box, Typography, Button, Collapse } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useCustomTheme } from '../../contexts/ThemeContext';
import { BlogSection, User, UserAccountTypes } from '../../workers/ApiWorker';
import DescriptionArea from '../upload/DescriptionArea';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import TextInput from '../universal/inputs/TextInput';
import RichTextInput from '../universal/inputs/RichTextInput';
import { DELETE_UPLOAD_FAILED } from '../../helpers/Messages';
import { useApi } from '../../contexts/ApiContext';
import { createWorkerFactory, useWorker } from '@shopify/react-web-worker';
import { useNavigate } from 'react-router-dom';
import ReactionsBar from '../reactions/ReactionsBar';
import { isAxiosError } from 'axios';

interface BlogSectionProps {
    blogUuid: string;
    section: BlogSection;
    isMobile: boolean;
    live?: string | null; // allow null explicitly
    index: number; // ← new
    onDeleteSection: (index: number, uuid: string) => void; // ← new
    totalSections: number;
    user?: User;
}

// Validation Schema
const validationSchema = Yup.object({
    name: Yup.string().trim().required('Section name is required'),
    content: Yup.string().required('Content is required'),
});

const createApiWorker = createWorkerFactory(
    () => import('../../workers/ApiWorker')
);

const BlogSectionArea: React.FC<BlogSectionProps> = ({
    blogUuid,
    section,
    isMobile,
    live,
    index,
    onDeleteSection,
    totalSections,
    user
}) => {
    const [sectionData, setSectionData] = useState<BlogSection>(section);
    const navigate = useNavigate(); // Hook for navigation
    const apiWorker = useWorker(createApiWorker);
    const { token } = useApi();
    const { theme } = useCustomTheme();
    const [openEditor, setOpenEditor] = useState(false);

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 900,
                margin: '0 auto',
                marginBottom:
                    index === totalSections - 1 &&
                    (!user ||
                        user.userProfile.accountType ===
                            UserAccountTypes.MEMBER)
                        ? 10
                        : 4,
                textAlign: 'left',
                mt: 4,
                px: 2,
                '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: 2,
                    display: 'block',
                    margin: '12px 0',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: -3,
                }}
            >
                {/* LEFT: Section Title */}
                <Typography
                    variant="h4"
                    sx={{
                        color: theme.palette.text.primary,
                    }}
                >
                    {sectionData.name}
                </Typography>

                {/* RIGHT: Submitted By */}
                <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.disabled, marginTop: 1 }}
                >
                    Submitted By&nbsp;&nbsp;
                    <Typography
                        component="span"
                        variant="caption"
                        sx={{
                            color: theme.palette.warning.main,
                            marginTop: 0.2,
                            cursor: 'pointer',
                            position: 'relative',
                        }}
                        onClick={() => {
                            navigate(`/${sectionData.author.username}`, {
                                state: {
                                    userProfileUuid: sectionData.author.uuid,
                                    fresh: true,
                                },
                            });
                        }}
                    >
                        {sectionData.author.username.toUpperCase()}
                    </Typography>
                </Typography>
            </Box>

            <Box sx={{ textAlign: 'left' }}>
                <DescriptionArea
                    removeMargin
                    description={sectionData.content}
                />
            </Box>

            <ReactionsBar
                user={user}
                reactions={sectionData.reactions || []}
                canReact={!!live} // only allow adding/toggling when live
                onToggleReaction={async (emoji) => {
                    if (token) {
                        try {
                            const response =
                                await apiWorker.postBlogSectionReaction(
                                    blogUuid,
                                    sectionData.uuid,
                                    emoji,
                                    token
                                );
                            setSectionData(response.data);
                        } catch (error) {
                            return null;
                        }
                    }
                }}
            />

            {user &&
                user.userProfile.accountType !== UserAccountTypes.MEMBER && (
                    <Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant={'outlined'}
                                color={openEditor ? 'inherit' : 'warning'}
                                onClick={() => setOpenEditor((v) => !v)}
                            >
                                {openEditor ? 'Cancel' : 'Edit Section'}
                            </Button>

                            {/* ✅ Delete button next to Edit */}
                            {totalSections > 1 && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() =>
                                        onDeleteSection(index, sectionData.uuid)
                                    }
                                >
                                    Delete Section
                                </Button>
                            )}
                        </Box>
                        <Collapse in={openEditor} unmountOnExit>
                            <Formik
                                enableReinitialize
                                initialValues={{
                                    name: sectionData.name,
                                    content: sectionData.content,
                                }}
                                validationSchema={validationSchema}
                                onSubmit={async (values, { resetForm }) => {
                                    // TODO
                                    // setError(undefined);

                                    if (!token) {
                                        return;
                                    }

                                    const updatedSection = {
                                        ...sectionData, // clone everything
                                        ...values, // overwrite name/content with form values
                                    };

                                    try {
                                        await apiWorker.updateBlogSection(
                                            updatedSection,
                                            token,
                                            blogUuid,
                                            sectionData.uuid
                                        );

                                        setOpenEditor(false);
                                        setSectionData(updatedSection);
                                    } catch (error) {
                                        // TODO
                                        //  setError(DELETE_UPLOAD_FAILED);
                                    }
                                }}
                            >
                                {({
                                    errors,
                                    touched,
                                    values,
                                    handleChange,
                                    isSubmitting,
                                    handleBlur,
                                    isValid,
                                    setFieldValue,
                                }) => {
                                    const hasChanges =
                                        values.name.trim() !==
                                            sectionData.name.trim() ||
                                        values.content.trim() !==
                                            sectionData.content.trim();

                                    return (
                                        <Form>
                                            {/* Section Name */}
                                            <TextInput
                                                id={'name'}
                                                label={'SECTION NAME *'}
                                                value={values.name}
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                                hasSubmitted={isSubmitting}
                                                touched={touched.name}
                                                errors={errors.name}
                                                isMobile={isMobile}
                                            />

                                            {/* Section Content */}
                                            <RichTextInput
                                                images={true}
                                                id={'content'}
                                                label={'CONTENT *'}
                                                value={values.content}
                                                errors={errors.content}
                                                handleBlur={handleBlur}
                                                hasSubmitted={isSubmitting}
                                                touchedFormik={touched.content}
                                                handleChange={setFieldValue}
                                                isMobile={isMobile}
                                            />
                                            {hasChanges && (
                                                <Button
                                                    sx={{
                                                        marginTop: 1,
                                                    }}
                                                    type="submit"
                                                    disabled={
                                                        isSubmitting || !isValid
                                                    }
                                                    variant="contained"
                                                >
                                                    UPDATE SECTION
                                                </Button>
                                            )}
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </Collapse>
                    </Box>
                )}
        </Box>
    );
};

export default BlogSectionArea;
